import AsyncStorage from '@react-native-async-storage/async-storage'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'

import moralis from './Moralis'
import {
  TEthereumChains,
  TMetadataSolana,
  TNftOwnerChain,
  TSolanaChains,
  TSplNft,
  TSplNftChain,
  TSplNftMetadata
} from './types'
import WebSockets from './WebSockets'

export const ethNetworksChains: TEthereumChains[] = ['eth', 'polygon', 'bsc', 'goerli']

export const solNetworksChains: TSolanaChains[] = ['mainnet', 'devnet']

export const Web3Store = types
  .model('Web3Store', {
    eth_address: types.optional(types.string, ''),
    sol_address: types.optional(types.string, ''),
    moralisServerUrl: types.optional(types.string, 'https://parser.api.vatominc.com/server'),
    moralisAppId: types.optional(types.string, 'KPwA7E3j_p5Pvme1XRhkF')
  })

  .volatile(self => {
    const { moralisServerUrl, moralisAppId } = self
    moralis.setAsyncStorage(AsyncStorage)
    moralis.start({ serverUrl: moralisServerUrl, appId: moralisAppId })
    const sockets = new WebSockets(moralis)
    return {
      moralis,
      sockets
    }
  })
  .actions(self => ({
    setEthAddress(address: string) {
      self.eth_address = address
    },
    setSolanaAddress(address: string) {
      self.sol_address = address
    }
  }))

  .actions(self => ({
    async getNfts(address: string, chains?: TEthereumChains[]) {
      self.setEthAddress(address)
      const moralis = self.moralis
      await moralis.User.currentAsync()

      if (chains && chains.length > 0) {
        ethNetworksChains.concat(chains)
      }

      const response = await Promise.all(
        ethNetworksChains.map(async chain => {
          const options = { address: self.eth_address, chain }
          const moralis_result = await moralis.Web3API.account.getNFTs(options)

          const tokens = moralis_result.result?.map(token => {
            const t = token as TNftOwnerChain
            t.chain = chain
            return t
          })

          return tokens || []
        })
      )
      const allTokens = response.reduce(
        (accumulator, currentValue) => accumulator.concat(currentValue),
        []
      )

      return allTokens
    },
    async getSolanaNfts(address: string) {
      self.setSolanaAddress(address)
      const moralis = self.moralis
      await moralis.User.currentAsync()
      const SolanaAPI: any = moralis.SolanaAPI

      const tokens_by_chain = await Promise.all(
        solNetworksChains.map(async chain => {
          const options = {
            address: self.sol_address,
            network: chain
          }

          const solanaTokens = await SolanaAPI.account.getNFTs(options)

          const solanaTokensWithChain: TSplNftChain[] = solanaTokens.map((token: TSplNft) => {
            const tk = token as TSplNftChain
            tk.chain = chain
            return tk
          })

          return solanaTokensWithChain
        })
      )

      const allTokens = tokens_by_chain.reduce(
        (accumulator, currentValue) => accumulator.concat(currentValue),
        []
      )

      const tokens = await Promise.all(
        allTokens.map(async token => {
          const metaplex: { metaplex: TSplNftMetadata } = await SolanaAPI.nft.getNFTMetadata({
            network: token.chain,
            address: token.mint
          })

          const metadata = await fetch(metaplex.metaplex.metadataUri)
            .then(res => res.json())
            .then(result => result)

          metadata.address = token.associatedTokenAddress
          metadata.mintAddress = token.mint
          metadata.owner = {
            associatedTokenAccountAddress: token.associatedTokenAddress,
            address: address
          }

          return metadata as TMetadataSolana
        })
      )
      return tokens
    }
  }))

export interface Web3StoreType extends Instance<typeof Web3Store> {}
export type Web3StoreSnapshot = SnapshotOut<typeof Web3Store>
